import { FREE_COMPUTE_CREDITS, FREE_ROOM_SLOTS, MAX_ROOM_CAP } from './offers';

/** Permanent feature unlocks — distinct from consumable compute credits. */
export type FeatureKey =
  | 'panel_placement'
  | 'lidar_scan'
  | 'export_pdf'
  | 'advanced_physics';

/** Resolved entitlement snapshot from GET /api/auth/me. */
export interface ResolvedEntitlements {
  /** null = unlimited compute (legacy sub, lifetime, or unlimited grant). */
  compute_remaining: number | null;
  compute_total: number;
  max_rooms: number;
  features: FeatureKey[];
  /** True when the user still has a live Stripe subscription (manage in portal). */
  has_active_subscription: boolean;
  /** Grandfathered unlimited access (lifetime, founder, legacy premium). */
  legacy_unlimited: boolean;
}

export interface UserProfile {
  id?: string;
  email?: string;
  entitlements?: FeatureKey[] | string[];
  isPro?: boolean;
}

export function hasFeature(
  ent: ResolvedEntitlements | null | undefined,
  feature: FeatureKey,
): boolean {
  if (!ent) return false;
  if (ent.legacy_unlimited) return true;
  return ent.features.includes(feature);
}

export function hasPanelPlacement(ent: ResolvedEntitlements | null | undefined): boolean {
  return hasFeature(ent, 'panel_placement');
}

export function hasUnlimitedCompute(ent: ResolvedEntitlements | null | undefined): boolean {
  if (!ent) return false;
  return ent.legacy_unlimited || ent.compute_remaining === null;
}

/** Everything the store sells is already the account's: unlimited compute,
 *  panel placement and every room (the Unlimited offer, or a grandfathered
 *  plan). The one question a header asks before offering an upgrade — there
 *  is nothing left to buy. */
export function ownsEverything(ent: ResolvedEntitlements | null | undefined): boolean {
  if (!ent) return false;
  if (ent.legacy_unlimited) return true;
  return hasUnlimitedCompute(ent) && hasPanelPlacement(ent) && ent.max_rooms >= MAX_ROOM_CAP;
}

/** `ownsEverything` from a raw `/api/auth/me` body, for the readers that do
 *  not hold a parsed wallet (the server-rendered headers, the landing nav).
 *  A grandfathered plan owns everything whatever the vector says, and a
 *  missing or malformed vector (an older backend) owns nothing more. */
export function ownsEverythingFromMe(
  me: { has_subscription?: unknown; is_premium?: unknown; resolved_entitlements?: unknown } | null | undefined,
): boolean {
  if (!me) return false;
  if (me.has_subscription === true || me.is_premium === true) return true;
  const r = me.resolved_entitlements as Partial<ResolvedEntitlements> | null | undefined;
  if (!r || typeof r !== 'object' || !Array.isArray(r.features)) return false;
  return ownsEverything({
    compute_remaining: r.compute_remaining === null ? null
      : typeof r.compute_remaining === 'number' ? r.compute_remaining : 0,
    compute_total: typeof r.compute_total === 'number' ? r.compute_total : 0,
    max_rooms: typeof r.max_rooms === 'number' ? r.max_rooms : 1,
    features: r.features,
    has_active_subscription: r.has_active_subscription === true,
    legacy_unlimited: r.legacy_unlimited === true,
  });
}

/** @deprecated Prefer hasFeature / ResolvedEntitlements from /api/auth/me. */
export function hasEntitlement(
  user: UserProfile | null | undefined,
  feature: FeatureKey,
): boolean {
  if (!user) return false;
  if (user.isPro) return true;
  if (Array.isArray(user.entitlements)) {
    return user.entitlements.includes(feature);
  }
  return false;
}

export function emptyEntitlements(computeTotal = FREE_COMPUTE_CREDITS): ResolvedEntitlements {
  return {
    compute_remaining: computeTotal,
    compute_total: computeTotal,
    max_rooms: FREE_ROOM_SLOTS,
    features: [],
    has_active_subscription: false,
    legacy_unlimited: false,
  };
}

export function clampMaxRooms(n: number): number {
  return Math.min(Math.max(1, n), MAX_ROOM_CAP);
}
